import {
  type AmsBrowseAccountFieldsFragment,
  type AmsBrowseCapabilitiesQuery,
  type AmsBrowseFilesQuery,
  AmsBrowseFileType,
  type AmsBrowsePolicyFieldsFragment,
  AmsBrowsePolicyStatus,
  type IntegrationType
} from '~/api/graphql';
import type { AmsRef } from './ams-ref';
import {
  ANY_DATE,
  type DatePreset,
  type DateWindow,
  dayFromToday,
  windowSet
} from './filter-chips';

export type Capability = AmsBrowseCapabilitiesQuery['amsBrowseCapabilities'][number];
export type Account = AmsBrowseAccountFieldsFragment;
export type Policy = AmsBrowsePolicyFieldsFragment;
export type BrowseFile = AmsBrowseFilesQuery['amsBrowseFiles']['files'][number];

/** One picked record, keyed in the selection by amsRefKey(ref). */
export type Picked = {
  ref: AmsRef;
  displayName: string;
  capability: Capability;
  accountName?: string;
};

/**
 * One AMS account, in the shape a caller needs to store a reference to it.
 * No knowledge-graph entity id: the browser names a record by AMS identity,
 * and a link is keyed on the same pair.
 */
export type AccountPick = {
  integrationId: string;
  integrationType: IntegrationType;
  /** The AMS's own identifier for the account. */
  externalId: string;
  displayName: string;
};

export type Toggle = (pick: Picked) => void;
export type IsSelected = (ref: AmsRef) => boolean;

export type SearchTab = 'accounts' | 'policies';
export type AccountTab = 'policies' | 'certificates' | 'files';

export type PolicyFilters = {
  query: string;
  statuses: AmsBrowsePolicyStatus[];
  expires: DateWindow;
};
export type CertificateFilters = { query: string; issued: DateWindow };
export type FileFilters = {
  query: string;
  attached: DateWindow;
  types: AmsBrowseFileType[];
  /** Folder names from the top, as the Folder chip's tags. */
  folder: string[];
};
export type AccountFilters = {
  policies: PolicyFilters;
  certificates: CertificateFilters;
  files: FileFilters;
};

export const NO_POLICY_FILTERS: PolicyFilters = { query: '', statuses: [], expires: ANY_DATE };
export const NO_CERTIFICATE_FILTERS: CertificateFilters = { query: '', issued: ANY_DATE };
export const NO_FILE_FILTERS: FileFilters = {
  query: '',
  attached: ANY_DATE,
  types: [],
  folder: []
};
export const NO_ACCOUNT_FILTERS: AccountFilters = {
  policies: NO_POLICY_FILTERS,
  certificates: NO_CERTIFICATE_FILTERS,
  files: NO_FILE_FILTERS
};

export const isFiltered = (filters: PolicyFilters | CertificateFilters | FileFilters): boolean => {
  if (filters.query.trim()) return true;
  if ('statuses' in filters) return filters.statuses.length > 0 || windowSet(filters.expires);
  if ('issued' in filters) return windowSet(filters.issued);
  return filters.types.length > 0 || filters.folder.length > 0 || windowSet(filters.attached);
};

export type View =
  | { kind: 'search' }
  | { kind: 'account'; capability: Capability; account: Account; tab: AccountTab }
  | {
      kind: 'policy';
      capability: Capability;
      account: Account;
      policy: Policy;
      /** Where Back goes: the search the policy was found in, or its account. */
      backTo: 'search' | 'account';
    };

export const ACCOUNT_PAGE_SIZE = 25;
export const FILE_PAGE_SIZE = 50;
export const ALL_INTEGRATIONS = 'all';

export const PAST_PRESETS: ReadonlyArray<DatePreset> = [
  { value: '30', label: 'Past 30 days', range: () => ({ from: dayFromToday(-30) }) },
  { value: '90', label: 'Past 90 days', range: () => ({ from: dayFromToday(-90) }) },
  { value: '365', label: 'Past year', range: () => ({ from: dayFromToday(-365) }) }
];

export const EXPIRY_PRESETS: ReadonlyArray<DatePreset> = [
  {
    value: 'next30',
    label: 'Next 30 days',
    range: () => ({ from: dayFromToday(0), to: dayFromToday(30) })
  },
  {
    value: 'next90',
    label: 'Next 90 days',
    range: () => ({ from: dayFromToday(0), to: dayFromToday(90) })
  },
  { value: 'expired', label: 'Already expired', range: () => ({ to: dayFromToday(-1) }) }
];

export const STATUS_OPTIONS: ReadonlyArray<{ value: AmsBrowsePolicyStatus; label: string }> = [
  { value: AmsBrowsePolicyStatus.Active, label: 'Active' },
  { value: AmsBrowsePolicyStatus.Cancelled, label: 'Cancelled' },
  { value: AmsBrowsePolicyStatus.Expired, label: 'Expired' },
  { value: AmsBrowsePolicyStatus.NonRenewed, label: 'Non-renewed' },
  { value: AmsBrowsePolicyStatus.Quote, label: 'Quote' }
];

export const FILE_TYPE_OPTIONS: ReadonlyArray<{ value: AmsBrowseFileType; label: string }> = [
  { value: AmsBrowseFileType.Pdf, label: 'PDF' },
  { value: AmsBrowseFileType.Document, label: 'Documents' },
  { value: AmsBrowseFileType.Spreadsheet, label: 'Spreadsheets' },
  { value: AmsBrowseFileType.Image, label: 'Images' },
  { value: AmsBrowseFileType.Email, label: 'Emails' }
];

/** A file's name with its extension, which the downloaded file is saved under. */
export const fileDisplayName = (file: Pick<BrowseFile, 'title' | 'extension'>): string => {
  const extension = file.extension?.replace(/^\./, '');
  if (!extension || file.title.toLowerCase().endsWith(`.${extension.toLowerCase()}`)) {
    return file.title;
  }
  return `${file.title}.${extension}`;
};

export const policyLabel = (policy: Policy): string => policy.policyNumber ?? 'Policy';

/** The parts that are set, joined as a row's secondary line. */
export const joinParts = (...parts: Array<string | null | undefined>): string | undefined =>
  parts.filter(Boolean).join(' · ') || undefined;

/**
 * Whether the server would search by nothing in the query: a name needs a word
 * of three or more characters, unlike an email or a seven-digit phone number.
 */
export const isTooShortToSearch = (query: string): boolean =>
  !query.includes('@') &&
  (query.match(/\d/g)?.length ?? 0) < 7 &&
  query.split(/\s+/).every((word) => word.length < 3);

export const accountSecondary = (account: Account): string | undefined =>
  joinParts(account.lookupCode, account.email, account.phone, account.address);
